import axios from 'axios';
import { isAppMode, isCheckoutRequest, postToApp } from './appMode';

const BACKEND_URL = window.location.origin;

// Create axios instance with default config
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (isAppMode() && (config.method || 'get').toLowerCase() === 'post' && isCheckoutRequest(config.url)) {
      postToApp({ type: 'blocked_checkout' });
      const err = new Error('in_app_checkout_blocked');
      err.response = { status: 403, data: { detail: "Purchases aren't available inside the app. Please visit rgmstudio.org." } };
      return Promise.reject(err);
    }
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors + 402 upgrade required
// FIXED: Only clear tokens for explicit auth endpoints, not random 401s during navigation
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (isAppMode() && url.includes('/auth/me')) postToApp({ type: 'session_expired' });
      // Only clear session for explicit auth validation failures
      // Don't logout for optional endpoints or random API calls that happen to return 401
      const isAuthEndpoint = url.includes('/auth/me') || url.includes('/auth/login') || url.includes('/auth/google-session');
      const isExplicitAuthFailure = isAuthEndpoint && error.response?.data?.detail?.includes('expired');
      
      if (isExplicitAuthFailure) {
        localStorage.removeItem('session_token');
        document.cookie = 'session_token=; path=/; max-age=0';
        // Only redirect if on a protected page
        if (window.location.pathname.includes('/dashboard') || 
            window.location.pathname.includes('/admin') || 
            window.location.pathname.includes('/submit') ||
            window.location.pathname.includes('/profile/edit')) {
          window.location.href = '/login';
        }
      }
    }
    // iter_191 — 402 upgrade-required interceptor. Fires a browser-wide
    // custom event any consumer can listen to (App root does, opens the
    // UpgradeModal). Callers can still handle 402 locally via try/catch.
    // iter_224 — Only dispatch when the backend explicitly asks for an
    // upgrade (code === 'upgrade_required' | 'producer_mode_required').
    // Other 402s — beat license purchase required, insufficient coins,
    // etc. — are per-request payment prompts, not tier upgrades. Firing
    // the global modal on those wrongly told a Founding Artist to
    // "Upgrade to Premium" when they simply needed to complete a
    // per-beat license checkout. Callers already toast those locally.
    if (error.response?.status === 402 && typeof window !== 'undefined') {
      const detail = error.response?.data?.detail || {};
      const code = typeof detail === 'object' ? detail.code : null;
      const isUpgradeCode = code === 'upgrade_required' || code === 'producer_mode_required';
      if (isUpgradeCode) {
        try {
          window.dispatchEvent(new CustomEvent('rgm:upgrade-required', {
            detail: {
              feature: detail.feature,
              currentTier: detail.current_tier,
              requiredTier: detail.required_tier,
              message: detail.message,
              code: detail.code,
            },
          }));
        } catch { /* older browsers */ }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
export { BACKEND_URL };
